import { trpcAny } from '@trashlab/api/src/library/frontend/utils/trpc';
import type { TRPCRequestOptions } from '@trpc/client';
import {
  UseTRPCInfiniteQueryOptions,
  UseTRPCInfiniteQueryResult,
  UseTRPCMutationOptions,
  UseTRPCMutationResult,
  UseTRPCQueryResult,
} from '@trpc/react-query/shared';
import { DefaultErrorShape } from '@trpc/server';
import { z, ZodIssue } from 'zod';

export function createMutationHook<
  T extends { path: string; input: any; output: any },
>(c: T['path']) {
  type Mutate = T['input'] extends never | Nil
    ? (input?: undefined, options?: TRPCRequestOptions) => Promise<T['output']>
    : (input: T['input'], options?: TRPCRequestOptions) => Promise<T['output']>;

  type Input = T['input'] extends never | Nil ? void : T['input'];

  type UseMutationContext = {
    mutate: Mutate;
  };

  return {
    useMutation: (opts?: UseTRPCMutationOptions<Input, any, T['output']>) => {
      const mutation = trpcAny[c].useMutation(opts);

      return mutation as UseTRPCMutationResult<
        T['output'],
        ErrorShape,
        Input,
        unknown
      >;
    },
    output: undefined as unknown as T['output'],
    input: undefined as unknown as T['input'],
    useContext: (() => {
      return trpcAny.useContext().client[c];
    }) as () => UseMutationContext,
  };
}

type ErrorShape = DefaultErrorShape & {
  shape: 'zod';
  data: DefaultErrorShape['data'] & {
    metadata?: Record<string, string>;
    zodError?: ZodIssue[];
  };
};

type Nil = { _nil: true };

type Options<T = any> = {
  trpc?: { abortOnUnmount?: boolean };
  enabled?: boolean;
  suspense?: boolean;
  retry?: ((failureCount: number, error: any) => boolean) | boolean | number;
  staleTime?: number;
  cacheTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
  refetchInterval?: number | false;
  refetchOnReconnect?: boolean;
  keepPreviousData?: boolean;
  onError?: (err: ErrorShape) => void;
  onSuccess?: (data: T) => void;
};

export function createQueryHook<
  T extends { path: string; output: any; input: any },
>(c: T['path']) {
  type QueryInput = T['input'] extends never
    ? undefined
    : T['input'] extends Nil
    ? undefined
    : T['input'];
  type UseQueryFn = T['input'] extends never
    ? (
        q?: undefined,
        options?: Options<T['output']>,
      ) => UseTRPCQueryResult<T['output'], ErrorShape>
    : T['input'] extends Nil
    ? (
        input?: undefined,
        options?: Options<T['output']>,
      ) => UseTRPCQueryResult<T['output'], ErrorShape>
    : (
        input: T['input'],
        op?: Options<T['output']>,
      ) => UseTRPCQueryResult<T['output'], ErrorShape>;

  type UseInfiniteQuery = (
    input: T['input'],
    opts: UseTRPCInfiniteQueryOptions<any, any, any, any>,
  ) => UseTRPCInfiniteQueryResult<T['output'], Error>;

  type UseQueryContext = {
    /** `opts` forwards to react-query's fetchQuery — pass `{staleTime: 0}` to force a
     *  network fetch past the app's global staleTime (a cached-stale read poisoned the
     *  voice live-transcript backfill, ai-observer-takeover · g round 2) */
    fetch: (
      input: T['input'],
      opts?: { staleTime?: number },
    ) => Promise<T['output']>;
    refetch: () => Promise<void>;
    invalidate: (input?: T['input']) => Promise<void>;
    cancel: (input?: T['input']) => Promise<void>;
    reset: (input?: T['input']) => Promise<void>;
    getData: () => T['output'] | undefined;
    setData: (
      input: QueryInput,
      data:
        | T['output']
        | undefined
        | ((previous: T['output'] | undefined) => T['output'] | undefined),
    ) => void;
  };

  return {
    useQuery: ((queryInput: any, options?: any) => {
      const query = trpcAny[c].useQuery(queryInput, options);
      return query;
    }) as UseQueryFn,

    useInfiniteQuery: ((queryInput, opts) => {
      const query = trpcAny[c].useInfiniteQuery(queryInput, opts);
      return query;
    }) as UseInfiniteQuery,
    path: c,
    output: undefined as unknown as T['output'],
    input: undefined as unknown as T['input'],
    useContext: (() => {
      return trpcAny.useContext()[c];
    }) as () => UseQueryContext,
  };
}
